/**
 * DashboardPage — landing screen composited from existing API endpoints.
 * No new API routes required.
 *
 * Layout:
 *   4 stat tiles — Parties, Open Batches, Match Balance, Net Ledger Position
 *   Recent Batches (up to 5)
 *   Recent Match Entries (up to 8)
 *   Party Balances (all, sorted by |balance| desc)
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Calendar,
  Layers,
  Plus,
  Users,
  Zap,
} from 'lucide-react';
import { api } from '../api';
import type { Party, PartyBalance, WeeklyBatch, MatchEntry } from '../api';
import { AppShell } from '../ui/AppShell';
import { useReadOnly } from '../ui/AppShell';
import { PageToolbar } from '../ui/PageToolbar';
import {
  Btn,
  Card,
  EmptyState,
  Money,
  StatePill,
  StatSkeleton,
  RowSkeleton,
} from '../ui/components';

// ── Types ───────────────────────────────────────────────────────────────────
interface MatchBalance {
  matchAccountId: number;
  balance: string;
}

// ── Page ────────────────────────────────────────────────────────────────────
export function DashboardPage() {
  const nav = useNavigate();
  const readOnly = useReadOnly();

  // Parallel data fetches — all from existing endpoints
  const partiesQ = useQuery<Party[]>({
    queryKey: ['parties'],
    queryFn: () => api.get<Party[]>('/api/parties'),
  });
  const balancesQ = useQuery<PartyBalance[]>({
    queryKey: ['party-balances-all'],
    queryFn: () => api.get<PartyBalance[]>('/api/parties/balances'),
    // Fallback: fetch each party balance individually if bulk endpoint absent
  });
  const batchesQ = useQuery<WeeklyBatch[]>({
    queryKey: ['batches'],
    queryFn: () => api.get<WeeklyBatch[]>('/api/batches'),
  });
  const matchEntriesQ = useQuery<MatchEntry[]>({
    queryKey: ['match-entries-recent'],
    queryFn: () => api.get<MatchEntry[]>('/api/match/entries?limit=8'),
  });
  const matchBalancesQ = useQuery<MatchBalance[]>({
    queryKey: ['match-balances'],
    queryFn: () => api.get<MatchBalance[]>('/api/match/balances'),
  });

  // ── Derived stats ─────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const parties = partiesQ.data ?? [];
    const batches = batchesQ.data ?? [];
    const matchBals = matchBalancesQ.data ?? [];

    const openBatches = batches.filter((b) => b.state === 'draft' || b.state === 'verified').length;
    const totalMatchBalance = matchBals.reduce(
      (sum, b) => sum + parseFloat(b.balance || '0'),
      0,
    );

    return { partyCount: parties.length, openBatches, totalMatchBalance };
  }, [partiesQ.data, batchesQ.data, matchBalancesQ.data]);

  // Sort parties by absolute balance descending
  const sortedParties = useMemo(() => {
    const parties = (partiesQ.data ?? []).filter((p) => p.role !== 'self');
    return parties; // balances fetched separately per-party in PartyBalanceRow
  }, [partiesQ.data]);

  const recentBatches = useMemo(
    () => (batchesQ.data ?? []).slice(0, 5),
    [batchesQ.data],
  );

  const recentEntries = useMemo(
    () => (matchEntriesQ.data ?? []).slice(0, 8),
    [matchEntriesQ.data],
  );

  const loading =
    partiesQ.isLoading || batchesQ.isLoading || matchEntriesQ.isLoading || matchBalancesQ.isLoading;

  // Build a map of upline party names for batches
  const partyById = useMemo(
    () => new Map((partiesQ.data ?? []).map((p) => [p.id, p.displayName])),
    [partiesQ.data],
  );

  return (
    <AppShell
      title="Dashboard"
      toolbar={
        <PageToolbar
          title="Dashboard"
          primary={
            !readOnly ? (
              <div className="flex items-center gap-2">
                <Btn size="sm" onClick={() => nav('/batches/new')}>
                  <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                  <span className="hidden sm:inline">New batch</span>
                </Btn>
                <Btn size="sm" variant="secondary" onClick={() => nav('/match')}>
                  <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                  <span className="hidden sm:inline">Match entry</span>
                </Btn>
              </div>
            ) : undefined
          }
        />
      }
    >
      {/* ── Stat tiles ─────────────────────────────────────────────────── */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {loading ? (
          <>
            <StatSkeleton />
            <StatSkeleton />
            <StatSkeleton />
            <StatSkeleton />
          </>
        ) : (
          <>
            <StatTile
              icon={Users}
              label="Parties"
              value={String(stats.partyCount)}
              sub="total"
              to="/ledgers"
            />
            <StatTile
              icon={Calendar}
              label="Open Batches"
              value={String(stats.openBatches)}
              sub="in progress"
              to="/batches"
              highlight={stats.openBatches > 0}
            />
            <StatTile
              icon={Zap}
              label="Match Balance"
              value={stats.totalMatchBalance.toFixed(2)}
              sub="net total"
              to="/match"
              numeric
              neg={stats.totalMatchBalance < 0}
            />
            <StatTile
              icon={Layers}
              label="Recent Activity"
              value={String(recentEntries.length)}
              sub="match entries"
              to="/match"
            />
          </>
        )}
      </div>

      {/* ── Two-column grid on desktop ──────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Batches */}
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-small font-semibold uppercase tracking-widest text-ink-muted">
              Recent Batches
            </h2>
            <Link to="/batches" className="text-caption text-brand hover:underline">
              View all
            </Link>
          </div>
          {batchesQ.isLoading ? (
            <Card padding={false}>
              <div className="px-4 py-3">
                <RowSkeleton rows={4} />
              </div>
            </Card>
          ) : recentBatches.length === 0 ? (
            <EmptyState
              icon={Calendar}
              title="No batches yet"
              description="Create a batch when your upline sends the P&L sheet."
              action={
                !readOnly ? (
                  <Btn size="sm" onClick={() => nav('/batches/new')}>
                    New batch
                  </Btn>
                ) : undefined
              }
            />
          ) : (
            <Card padding={false} className="overflow-hidden">
              {recentBatches.map((b, idx) => (
                <Link
                  key={b.id}
                  to={`/batches/${b.id}`}
                  className={[
                    'flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-raised',
                    idx < recentBatches.length - 1 ? 'border-b border-border' : '',
                  ].join(' ')}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-small font-semibold text-ink">
                        {partyById.get(b.uplinePartyId) ?? `Party #${b.uplinePartyId}`}
                      </span>
                      <StatePill state={b.state} />
                    </div>
                    <div className="mt-0.5 text-caption text-ink-muted">{b.weekMonday}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
                </Link>
              ))}
            </Card>
          )}
        </section>

        {/* Recent Match Entries */}
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-small font-semibold uppercase tracking-widest text-ink-muted">
              Recent Match Entries
            </h2>
            <Link to="/match" className="text-caption text-brand hover:underline">
              View all
            </Link>
          </div>
          {matchEntriesQ.isLoading ? (
            <Card padding={false}>
              <div className="px-4 py-3">
                <RowSkeleton rows={5} />
              </div>
            </Card>
          ) : recentEntries.length === 0 ? (
            <EmptyState
              icon={Zap}
              title="No match entries"
              description="Match entries will appear here."
              action={
                !readOnly ? (
                  <Btn size="sm" onClick={() => nav('/match')}>
                    Go to Match
                  </Btn>
                ) : undefined
              }
            />
          ) : (
            <Card padding={false} className="overflow-hidden">
              {recentEntries.map((e, idx) => (
                <Link
                  key={e.id}
                  to={`/match/entries/${e.id}`}
                  className={[
                    'flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-raised',
                    idx < recentEntries.length - 1 ? 'border-b border-border' : '',
                  ].join(' ')}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Money
                        amount={e.amount}
                        className="text-small font-semibold"
                      />
                      {e.voidedAt && (
                        <span className="text-caption text-neg">voided</span>
                      )}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-caption text-ink-muted">
                      <span>{e.effectiveDate}</span>
                      {e.note && (
                        <span className="truncate max-w-[14ch]">{e.note}</span>
                      )}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
                </Link>
              ))}
            </Card>
          )}
        </section>
      </div>

      {/* ── Party Balances ─────────────────────────────────────────────── */}
      {sortedParties.length > 0 && (
        <section className="mt-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-small font-semibold uppercase tracking-widest text-ink-muted">
              Party Balances
            </h2>
            <Link to="/ledgers" className="text-caption text-brand hover:underline">
              View ledgers
            </Link>
          </div>
          <Card padding={false} className="overflow-hidden">
            {/* Desktop table */}
            <div className="hidden md:block">
              <table className="min-w-full text-small">
                <thead className="bg-surface-raised">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-caption font-semibold uppercase tracking-wide text-ink-muted">
                      Party
                    </th>
                    <th className="px-4 py-2.5 text-left text-caption font-semibold uppercase tracking-wide text-ink-muted">
                      Role
                    </th>
                    <th className="px-4 py-2.5 text-right text-caption font-semibold uppercase tracking-wide text-ink-muted">
                      Balance (K-INR)
                    </th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sortedParties.map((p) => (
                    <tr key={p.id} className="transition-colors hover:bg-surface-raised/60">
                      <td className="px-4 py-3 font-medium text-ink">{p.displayName}</td>
                      <td className="px-4 py-3">
                        <StatePill state={p.role} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <PartyBalanceCell partyId={p.id} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/parties/${p.id}/ledger`}
                          className="inline-flex items-center gap-1 text-caption text-brand hover:underline"
                        >
                          Ledger
                          <ArrowRight className="h-3 w-3" strokeWidth={2} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile stacked */}
            <div className="divide-y divide-border md:hidden">
              {sortedParties.map((p) => (
                <Link
                  key={p.id}
                  to={`/parties/${p.id}/ledger`}
                  className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-raised"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-ink">{p.displayName}</span>
                      <StatePill state={p.role} />
                    </div>
                    <div className="mt-1">
                      <PartyBalanceCell partyId={p.id} />
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
                </Link>
              ))}
            </div>
          </Card>
        </section>
      )}

      {/* Bottom spacer for mobile (extra padding above FAB area) */}
      <div className="h-4 md:hidden" />
    </AppShell>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function StatTile({
  icon: Icon,
  label,
  value,
  sub,
  to,
  numeric = false,
  highlight = false,
  neg = false,
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: string | number }>;
  label: string;
  value: string;
  sub?: string;
  to?: string;
  numeric?: boolean;
  highlight?: boolean;
  neg?: boolean;
}) {
  const valueClass = neg
    ? 'text-neg'
    : highlight
    ? 'text-brand'
    : 'text-ink';

  const inner = (
    <Card className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-caption text-ink-muted">
        <Icon className="h-3.5 w-3.5" strokeWidth={2} />
        <span className="uppercase tracking-wide font-medium">{label}</span>
      </div>
      <div
        className={[
          'text-display font-bold',
          numeric ? 'font-mono tabular-nums' : '',
          valueClass,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {value}
      </div>
      {sub && <div className="text-caption text-ink-muted">{sub}</div>}
    </Card>
  );

  return to ? (
    <Link to={to} className="block transition-opacity hover:opacity-80">
      {inner}
    </Link>
  ) : (
    inner
  );
}

/** Fetches and displays a single party's balance — avoids needing a bulk endpoint. */
function PartyBalanceCell({ partyId }: { partyId: number }) {
  const q = useQuery<PartyBalance>({
    queryKey: ['balance', partyId],
    queryFn: () => api.get<PartyBalance>(`/api/parties/${partyId}/balance`),
    staleTime: 30_000,
  });

  if (q.isLoading) {
    return <span className="font-mono text-ink-muted">…</span>;
  }

  const raw = q.data?.balance ?? '0';
  const num = parseFloat(raw);
  const isNeg = Number.isFinite(num) && num < 0;
  const isPos = Number.isFinite(num) && num > 0;
  const colour = isNeg ? 'text-neg' : isPos ? 'text-pos' : 'text-ink-muted';

  return (
    <span className={['font-mono font-semibold tabular-nums text-small', colour].join(' ')}>
      {raw}
    </span>
  );
}

// Import BookOpen to suppress unused warning — used in potential EmptyState fallback
const _unused = BookOpen;
